import type { DamageEvent } from '../../services/damageModel';
import { applyComminution, coreRadiusFromFootprint, energyDensityFromEvent } from '../../services/comminution';
import { subdivideCell, type FractureCell } from '../../services/fracture/voronoiFracture';
import { queueShardFromCellVel } from './debrisPool';
import { noteLiveCapHit, worldCentroid, wouldExceedLiveCap } from './wallSetup';
import type { DemolitionImpact, DemolitionRuntime } from './types';

export function beginCarveComminution(rt: DemolitionRuntime) {
  rt.pendingPulverizedVolume = 0;
  rt.pendingShardVolume = 0;
  rt.pendingChunkVolume = 0;
}

export function commitCarveComminution(rt: DemolitionRuntime) {
  rt.lastPulverizedVolume = rt.pendingPulverizedVolume;
  rt.lastShardVolume = rt.pendingShardVolume;
  rt.lastSpawnedChunkVolume = rt.pendingChunkVolume;
  rt.sessionPulverizedVolume += rt.pendingPulverizedVolume;
  if (rt.lastResolve) {
    rt.lastResolve = { ...rt.lastResolve, pulverizedVolume: rt.lastPulverizedVolume };
  }
}

export function comminutionContextForEvent(event: DamageEvent) {
  const coreRadius = coreRadiusFromFootprint(event.footprint);
  return {
    coreRadius,
    energyDensity: energyDensityFromEvent(event.energy, coreRadius),
  };
}

export function refreshFragmentVolumes(rt: DemolitionRuntime) {
  const vols: number[] = [];
  for (const rec of rt.records.values()) {
    if (rec.state === 'dynamic') {
      vols.push(rec.volume);
      if (vols.length >= 300) break;
    }
  }
  rt.fragmentVolumes = vols;
}

/** Horizontal pile radius from live dynamic centroids (xz). */
export function computePileRadius(rt: DemolitionRuntime): number {
  let cx = 0;
  let cz = 0;
  let n = 0;
  const pts: Array<{ x: number; z: number }> = [];
  for (const rec of rt.records.values()) {
    if (rec.state !== 'dynamic' || !rec.bodyId) continue;
    const idx = rt.bodyIndex.get(rec.bodyId);
    let x = rec.worldCentroid.x;
    let z = rec.worldCentroid.z;
    if (idx != null) {
      x = rt.transforms[idx * 7];
      z = rt.transforms[idx * 7 + 2];
    }
    pts.push({ x, z });
    cx += x;
    cz += z;
    n += 1;
  }
  if (n < 2) return 1.5;
  cx /= n;
  cz /= n;
  let r = 0;
  for (const p of pts) {
    const d = Math.hypot(p.x - cx, p.z - cz);
    if (d > r) r = d;
  }
  return Math.max(r, 0.4);
}

/**
 * Subdivide a carved cell into N pieces (N = 1 + extraPieces).
 * Returns physics sub-cells registered on rt.cells; queues size-floor shards.
 * Pieces crushed by comminution are not spawned (volume → pulverizedVolume).
 * null → spawn parent whole.
 */
export function trySubdivideForCarve(
  rt: DemolitionRuntime,
  cell: FractureCell,
  impact: DemolitionImpact,
  impulseVel: { vx: number; vy: number; vz: number } | null,
  budget: { remaining: number },
  extraPieces: number,
  /** When true, parent is already in rt.cells and will be removed on success. */
  parentInLiveRegistry = false,
  comminutionCtx: { energyDensity: number; coreRadius: number } = {
    energyDensity: 0,
    coreRadius: 0,
  },
): FractureCell[] | null {
  const totalPieces = Math.max(2, 1 + extraPieces);
  if (budget.remaining < 1) return null;

  const raw = subdivideCell(cell, totalPieces, rt.subRng);
  if (raw.length < 2) return null;

  const classified = applyComminution({
    pieces: raw.map((sub) => {
      const wc = worldCentroid(rt, sub);
      return {
        volume: sub.volume,
        dist: Math.hypot(wc.x - impact.x, wc.y - impact.y, wc.z - impact.z),
      };
    }),
    params: rt.profile.comminution,
    energyDensity: comminutionCtx.energyDensity,
    coreRadius: comminutionCtx.coreRadius,
    sizeFloorVol: rt.profile.sizeFloorVol,
  });
  const pulverizeSet = new Set(classified.pulverizeIndices);

  const floor = rt.profile.sizeFloorVol;
  const physicsSubs: FractureCell[] = [];
  const shardSubs: FractureCell[] = [];
  const powderSubs: FractureCell[] = [];
  for (let i = 0; i < raw.length; i += 1) {
    const sub = raw[i];
    if (pulverizeSet.has(i)) {
      powderSubs.push(sub);
      continue;
    }
    if (sub.volume < floor) shardSubs.push(sub);
    else physicsSubs.push(sub);
  }

  if (physicsSubs.length > budget.remaining) return null;

  const netAdd = physicsSubs.length - (parentInLiveRegistry ? 1 : 0);
  if (netAdd > 0 && wouldExceedLiveCap(rt, netAdd)) {
    noteLiveCapHit(rt);
    return null;
  }

  for (const s of powderSubs) rt.pendingPulverizedVolume += s.volume;
  for (const s of shardSubs) {
    rt.pendingShardVolume += s.volume;
    queueShardFromCellVel(rt, s, impact, impulseVel);
  }

  if (physicsSubs.length === 0) {
    rt.removedChunks += 1;
    return [];
  }

  budget.remaining -= physicsSubs.length;

  const registered: FractureCell[] = [];
  for (const sub of physicsSubs) {
    const id = rt.nextCellId++;
    const owned: FractureCell = {
      ...sub,
      id,
      neighbors: [],
    };
    rt.cells.push(owned);
    rt.cellById.set(owned.id, owned);
    registered.push(owned);
  }
  return registered;
}

export function liveFragmentVolume(rt: DemolitionRuntime): number {
  let v = 0;
  for (const rec of rt.records.values()) {
    if (rec.state === 'static' || rec.state === 'dynamic') v += rec.volume;
  }
  return v;
}
